import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCutriteLinky } from "@/api/hooks/useCutrite";
import { queryKeys } from "@/api/queryKeys";
import type { CutriteLinka, CutriteOdoslanie, Varianta } from "@/api/schemas";

/**
 * Udalosť, ktorou pás otvorí okno Poslať do Cut Rite vo `VystupyMenu`.
 * `detail.varianta_ids` (KOLO 207) — okno otvorí výber toho zoznamu.
 */
export const OTVOR_CUTRITE = "tandem:otvor-cutrite";

export interface OtvorCutriteDetail {
  varianta_ids?: string[];
}

/**
 * Veta o zmene po optimalizácii (KOLO 153) — user: „keď zmeníš nejaký
 * rozmer… daj mi upozornenie, že musíš poslať do Cut Rite znova a potom
 * odoslať na pílu". Zoznam časti výroby (KOLO 218) povie, ktorá časť sa
 * zmenila — do KOLA 218 písal „Zákazka" aj pri jednej časti.
 */
export function vetaZmeny(
  o: CutriteOdoslanie,
  casti: readonly string[] = [],
): string {
  const kto =
    casti.length === 0
      ? "Zákazka sa zmenila"
      : casti.length === 1
        ? `Časť ${casti[0]} sa zmenila`
        : `Časti ${casti.join(" + ")} sa zmenili`;
  const ju = casti.length > 1 ? "ich" : "ju";
  return o.pila_stav === "hotovo"
    ? `${kto} po odoslaní na pílu — na píle je stará verzia. Pošli ${ju} znova do Cut Rite a potom na pílu a tlač plánov.`
    : `${kto} po optimalizácii v Cut Rite. Pošli ${ju} znova do Cut Rite, až potom na pílu a tlač plánov.`;
}

/** `AA6262_Skrinka_prava.csv` → `AA6262_Skrinka_prava` — ako v Cut Rite. */
export function menoZoznamu(nazovSuboru: string): string {
  return nazovSuboru.replace(/\.csv$/i, "");
}

/**
 * Veta pásu — pri zákazke s viac zoznamami (časti, KOLO 207) povie ktoré;
 * pri jednom ako do KOLA 207.
 */
export function vetaPasu(zmenene: CutriteLinka[], zoznamov: number): string {
  const [prvy] = zmenene;
  if (!prvy) return "";
  const casti = prvy.casti ?? [];
  if (zoznamov <= 1 || (zmenene.length === 1 && casti.length > 0)) {
    return vetaZmeny(prvy.posledne, casti);
  }
  if (zmenene.length === 1) {
    return `${menoZoznamu(prvy.nazov_suboru)}: ${vetaZmeny(prvy.posledne)}`;
  }
  const mena = zmenene.map((l) => menoZoznamu(l.nazov_suboru)).join(", ");
  return `Zmenili sa zoznamy v Cut Rite ${mena}. Pošli ich znova do Cut Rite, až potom na pílu a tlač plánov.`;
}

/**
 * Pás nad tabuľkou zákazky, kým sa rezanie niektorého zoznamu líši od
 * toho, čo dostal Cut Rite. Server to vie z `zmenene`; po každej úprave
 * varianty sa zoznamy načítajú znova.
 */
export function PasCutrite({
  zakazkaId,
  varianta,
}: {
  zakazkaId: string;
  varianta: Varianta;
}): JSX.Element | null {
  const qc = useQueryClient();
  const linky = useCutriteLinky(zakazkaId);
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: queryKeys.cutrite.linky(zakazkaId) });
  }, [qc, zakazkaId, varianta]);

  const vsetky = linky.data?.data ?? [];
  const zmenene = vsetky.filter((l) => l.posledne.zmenene);
  if (zmenene.length === 0) return null;
  // Otvoriť ten zoznam, ktorý sa dá z Rozpočtu znova poslať
  const otvorit = zmenene.find((l) => l.varianta_ids.length > 0);
  return (
    <div
      role="status"
      data-testid="pas-cutrite"
      className="flex shrink-0 items-center gap-2 border-b border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-900 dark:text-amber-200"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0">{vetaPasu(zmenene, vsetky.length)}</span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="ml-auto h-6 shrink-0 px-2 text-xs"
        onClick={() =>
          window.dispatchEvent(
            new CustomEvent<OtvorCutriteDetail>(OTVOR_CUTRITE, {
              detail: { varianta_ids: otvorit?.varianta_ids },
            }),
          )
        }
      >
        Poslať do Cut Rite
      </Button>
    </div>
  );
}
